class NtShippingEstimator extends HTMLElement {
  static observedAttributes = ['data-cart-key'];

  connectedCallback() {
    if (this.bound) return;
    this.bound = true;
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-nt-calculate]')) this.calculate();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target.matches('[data-nt-zip]')) {
        event.preventDefault();
        this.calculate();
      }
    });
    this.addEventListener('input', () => this.invalidate());
    this.addEventListener('change', () => this.invalidate());
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  attributeChangedCallback(name, before, after) {
    if (before && before !== after) this.invalidate();
  }

  get strings() {
    return JSON.parse(this.querySelector('[data-nt-shipping-strings]')?.textContent || '{}');
  }

  measure(name, parameters = {}) {
    document.dispatchEvent(new CustomEvent('nt:interaction', {
      detail: { name, parameters: {
        interaction_location: this.closest('dialog') ? 'cart_drawer' : 'cart_page',
        ...parameters,
      } },
    }));
  }

  invalidate() {
    this.controller?.abort();
    this.controller = null;
    const rates = this.querySelector('[data-nt-shipping-rates]');
    const status = this.querySelector('[data-nt-shipping-status]');
    if (rates && !rates.hidden) {
      rates.hidden = true;
      rates.replaceChildren();
      status.textContent = this.strings.changed;
    }
    this.busy(false);
  }

  busy(value) {
    const button = this.querySelector('[data-nt-calculate]');
    if (!button) return;
    if (button.disabled !== value) button.disabled = value;
    const label = value ? this.strings.loading : this.strings.calculate;
    if (button.textContent !== label) button.textContent = label;
    this.setAttribute('aria-busy', String(value));
  }

  async calculate() {
    const button = this.querySelector('[data-nt-calculate]');
    if (button.disabled) return;
    const input = this.querySelector('[data-nt-zip]');
    const state = this.querySelector('[data-nt-state]');
    const status = this.querySelector('[data-nt-shipping-status]');
    const list = this.querySelector('[data-nt-shipping-rates]');
    const zip = input.value.replace(/\D/g, '');
    list.hidden = true;
    list.replaceChildren();
    if (zip.length !== 8 || !state.value) {
      status.textContent = this.strings.invalid;
      this.measure('nt_shipping_quote_result', { quote_status: 'invalid_input' });
      (zip.length !== 8 ? input : state).focus();
      return;
    }
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    const timeout = setTimeout(() => controller.abort(), 22000);
    const started = performance.now();
    this.measure('nt_shipping_quote_start');
    this.busy(true);
    status.textContent = this.strings.loading;
    const params = new URLSearchParams({
      'shipping_address[zip]': zip,
      'shipping_address[country]': 'Brazil',
      'shipping_address[province]': state.value,
    });
    const root = window.Shopify?.routes?.root || '/';
    const request = async (path, method = 'GET') => {
      const response = await fetch(`${root}cart/${path}.json?${params}`, {
        method, signal: controller.signal, headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('shipping_unavailable');
      return response.json();
    };
    try {
      await request('prepare_shipping_rates', 'POST');
      let data = null;
      for (let attempt = 0; attempt < 12 && !data; attempt++) {
        if (controller.signal.aborted) throw new Error('aborted');
        if (attempt) await new Promise((resolve) => setTimeout(resolve, 1000));
        data = await request('async_shipping_rates');
      }
      if (!data) throw new Error('shipping_timeout');
      if (!data.shipping_rates?.length) {
        status.textContent = this.strings.empty;
        this.measure('nt_shipping_quote_result', { quote_status: 'no_rates', duration_ms: Math.round(performance.now() - started) });
        return;
      }
      const money = new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', {
        style: 'currency', currency: this.dataset.currency || 'BRL',
      });
      const rates = data.shipping_rates.filter((rate) => Number.isFinite(Number(rate.price)));
      rates.sort((a, b) => Number(a.price) - Number(b.price));
      if (!rates.length) throw new Error('shipping_invalid');
      for (const rate of rates) {
        const item = document.createElement('li');
        const name = document.createElement('span');
        const price = document.createElement('strong');
        name.textContent = rate.presentment_name || rate.name;
        const days = rate.delivery_days;
        if (Array.isArray(days) && days.length === 2 && days.every((day) => Number.isInteger(day) && day >= 0)) {
          const duration = document.createElement('small');
          const [min, max] = days;
          duration.textContent = min === max
            ? `${min} ${min === 1 ? this.strings.business_day : this.strings.business_days}`
            : `${min}–${max} ${this.strings.business_days}`;
          name.append(duration);
        }
        price.textContent = this.dataset.freeEligible === 'true' || Number(rate.price) === 0
          ? this.strings.free : money.format(Number(rate.price));
        item.append(name, price);
        list.append(item);
      }
      list.hidden = false;
      status.textContent = this.dataset.freeEligible === 'true' ? this.strings.free_ready : this.strings.ready;
      this.measure('nt_shipping_quote_result', {
        quote_status: 'success',
        rate_count: rates.length,
        lowest_rate: this.dataset.freeEligible === 'true' ? 0 : Number(rates[0].price),
        free_shipping: this.dataset.freeEligible === 'true',
        currency: this.dataset.currency || 'BRL',
        duration_ms: Math.round(performance.now() - started),
      });
    } catch (error) {
      if (this.controller === controller && this.isConnected) {
        status.textContent = this.strings.error;
        this.measure('nt_shipping_quote_result', {
          quote_status: controller.signal.aborted || error.message === 'shipping_timeout' ? 'timeout' : 'error',
          duration_ms: Math.round(performance.now() - started),
        });
      }
    } finally {
      clearTimeout(timeout);
      if (this.controller === controller) this.busy(false);
    }
  }
}

if (!customElements.get('nt-shipping-estimator')) customElements.define('nt-shipping-estimator', NtShippingEstimator);
